"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { capture } from "@/lib/posthog-browser";

/**
 * Fires a single PostHog `page_not_found` event when a not-found boundary
 * renders.
 *
 * Critical for visibility: Next's App Router serves notFound() during
 * client-side (soft) navigation as an HTTP 200 RSC response, so soft 404s
 * never appear in status-code-based logging (Vercel runtime logs, alerts).
 * This client island captures the attempted path regardless of hard vs
 * soft navigation, so missing/moved URLs surface in analytics.
 *
 * Capture goes through `lib/posthog-browser`, which queues the event until
 * the deferred SDK init finishes. A direct `posthog.capture()` here discarded
 * `page_not_found` on every cold load — and unlike `camp_viewed` there is no
 * server-side dual-write to fall back on, so the event was simply gone
 * (#2413). That helper also no-ops without NEXT_PUBLIC_POSTHOG_KEY, so this
 * stays safe on local dev and PR previews.
 */
export function NotFoundTracker() {
  const pathname = usePathname();
  useEffect(() => {
    capture("page_not_found", { path: pathname });
  }, [pathname]);

  return null;
}
